import { GuardMeta } from "xstate";
import {
  CashbackWizardContext,
  CashbackWizardEvent,
  NaturalPersonsEnum,
  WizardTypeEnum,
} from "@features/cashbackWizard2.0/types";
import { AccountTypeEnum } from "@features/cashbackWizard2.0/types/cashbackWizardContext";

export const isPrev = <T extends CashbackWizardEvent>(
  ctx: CashbackWizardContext,
  event: T,
  condMeta: GuardMeta<CashbackWizardContext, T>
) => condMeta.state.event.type === "PREV";

export const isNodeTransition = (ctx: CashbackWizardContext) =>
  ctx.isNodeTransition;

export const isNewUser = (ctx: CashbackWizardContext) =>
  ctx.wizardType === WizardTypeEnum.newUser;

export const isExistingUser = (ctx: CashbackWizardContext) =>
  ctx.wizardType === WizardTypeEnum.existingUser;

export const isSingleOrJoined = (ctx: CashbackWizardContext) =>
  ctx.accountHolderType === "single" || ctx.accountHolderType === "joined";

export const isMinorOrJoined = (ctx: CashbackWizardContext) =>
  ctx.accountHolderType === "minor" || ctx.accountHolderType === "joined";

export const isSingle = (ctx: CashbackWizardContext) =>
  ctx.accountHolderType === "single";

export const isMinor = (ctx: CashbackWizardContext) =>
  ctx.accountHolderType === "minor";

export const isJoined = (ctx: CashbackWizardContext) =>
  ctx.accountHolderType === "joined";

export const isNotNaturalPerson1 = (ctx: CashbackWizardContext) =>
  ctx.currentNaturalPerson !== NaturalPersonsEnum.naturalPerson1;

export const isNotNaturalPerson3 = (ctx: CashbackWizardContext) =>
  ctx.currentNaturalPerson !== NaturalPersonsEnum.naturalPerson3;

export const isNaturalPerson1 = (ctx: CashbackWizardContext) =>
  ctx.currentNaturalPerson === NaturalPersonsEnum.naturalPerson1;

export const isNaturalPerson2 = (ctx: CashbackWizardContext) =>
  ctx.currentNaturalPerson === NaturalPersonsEnum.naturalPerson2;

export const isNaturalPerson3 = (ctx: CashbackWizardContext) =>
  ctx.currentNaturalPerson === NaturalPersonsEnum.naturalPerson3;

export const isPrivateAccount = (ctx: CashbackWizardContext) =>
  ctx.accountType === AccountTypeEnum.PRIVATE;

export const isCompanyAccount = (ctx: CashbackWizardContext) =>
  ctx.accountType === AccountTypeEnum.COMPANY;

export const isDAB = (ctx: CashbackWizardContext) =>
  ctx.values.bic === "DABBDEMMXXX";

export const isFNZ = (ctx: CashbackWizardContext) =>
  ctx.values.bic === "COBADEMXXXX";

export const isComdirect = (ctx: CashbackWizardContext) =>
  ctx.values.bic === "COBADEHDXXX";

export const isFondsdepot = (ctx: CashbackWizardContext) =>
  ctx.values.bic === "FODBDE77XXX";

export const isFFB = (ctx: CashbackWizardContext) =>
  ctx.values.bic === "FFBKDEFFTHK";

export const isAusburger = (ctx: CashbackWizardContext) =>
  ctx.values.bic === "AUGBDE77XXX";

export const isMetzler = (ctx: CashbackWizardContext) =>
  ctx.values.bic === "METZDEFFXXX";

export const isDWS = (ctx: CashbackWizardContext) =>
  ctx.values.bic === "DEUTDEFFXXX";

export const isMorgenFund = (ctx: CashbackWizardContext) =>
  ctx.values.bic === "DEUTLU2DXXX";

export const isMorgenFundEvent = (event: CashbackWizardEvent) => {
  return event.type === "BANK_SELECTED" && event.bic === "DEUTLU2DXXX";
};

export const hasExistingAccountHolders = (ctx: CashbackWizardContext) =>
  ctx.accountHolders && ctx.accountHolders.length > 0;

export const isAuthenticated = (ctx: CashbackWizardContext) =>
  ctx.isAuthenticated;

export const isEditing = (ctx: CashbackWizardContext) => ctx.isEditing;

export const isNotEditing = (ctx: CashbackWizardContext) => !ctx.isEditing;

export const shouldShowTurnover = (ctx: CashbackWizardContext) => {
  if (!isComdirect(ctx) || !isExistingUser(ctx)) return false;

  return (
    (isNaturalPerson1(ctx) && (isSingle(ctx) || isMinor(ctx))) ||
    (isNaturalPerson2(ctx) && isJoined(ctx))
  );
};

export const isReducedFlow = (ctx: CashbackWizardContext) => {
  if (isCompanyAccount(ctx) || !isNewUser(ctx)) return false;

  if (isFNZ(ctx) && isSingleOrJoined(ctx)) {
    return true;
  }
  if (isComdirect(ctx) || isFFB(ctx)) {
    return true;
  }
  return false;
};

export const isNotReducedFlow = (ctx: CashbackWizardContext) =>
  !isReducedFlow(ctx);

const guardsMap = {
  isPrev,
  isNodeTransition,
  isNewUser,
  isExistingUser,
  isSingle,
  isMinor,
  isJoined,
  isSingleOrJoined,
  isMinorOrJoined,
  isNotNaturalPerson1,
  isNotNaturalPerson3,
  isNaturalPerson1,
  isNaturalPerson2,
  isNaturalPerson3,
  isPrivateAccount,
  isCompanyAccount,
  isDAB,
  isFNZ: isFNZ,
  isComdirect,
  isFondsdepot,
  isFFB,
  isMetzler,
  isAusburger,
  isDWS,
  isMorgenFund,
  hasExistingAccountHolders,
  isAuthenticated,
  isEditing,
  isNotEditing,
  isReducedFlow,
  isNotReducedFlow,
  shouldShowTurnover,
};

export type Guard<T extends CashbackWizardEvent> =
  | keyof typeof guardsMap
  | ((
      ctx: CashbackWizardContext,
      event: T,
      condMeta: GuardMeta<CashbackWizardContext, T>
    ) => boolean)
  | Guard<T>[];

export const checkConditions =
  <T extends CashbackWizardEvent>(guards: Guard<T>[]) =>
  (
    ctx: CashbackWizardContext,
    event: T,
    condMeta: GuardMeta<CashbackWizardContext, T>
  ): boolean =>
    guards.every((guardName) => {
      if (Array.isArray(guardName)) {
        return checkOrConditions(guardName)(ctx, event, condMeta);
      }
      if (typeof guardName === "function") {
        return guardName(ctx, event, condMeta);
      }
      const guard = guardsMap[guardName];
      return guard<T>(ctx, event, condMeta);
    });

export const checkOrConditions =
  <T extends CashbackWizardEvent>(guards: Guard<T>[]) =>
  (
    ctx: CashbackWizardContext,
    event: T,
    condMeta: GuardMeta<CashbackWizardContext, T>
  ): boolean =>
    guards.some((guardName) => {
      if (Array.isArray(guardName)) {
        return checkConditions<T>(guardName)(ctx, event, condMeta);
      }
      if (typeof guardName === "function") {
        return guardName(ctx, event, condMeta);
      }
      const guard = guardsMap[guardName];
      return guard<T>(ctx, event, condMeta);
    });
